import EditActionButton from '@components/EditActionButton';
import { Box, Button, IconButton, styled, Typography, useTheme } from '@mui/material';
import DownloadTalentCvButton from '@modules/cv-generator';
import { useState } from 'react';
import { SectionCard } from './SectionCard';
import TalentForm from './TalentForm/TalentForm';

const Divider = styled(Box)(({ theme }) => ({
    width: '1px',
    height: '16px',
    background: theme.palette.neutralDarkGray.main,
}));

interface TalentProfileCardProps {
    talent: any;
}

const GENDER_LABELS: Record<string, string> = {
    male: 'Homme',
    female: 'Femme',
    other: 'Autre',
};

const TalentProfileCard = ({ talent }: TalentProfileCardProps) => {
    const theme = useTheme();
    const [formOpen, setFormOpen] = useState(false);
    const joinedLabel = talent?.createdAt
        ? new Date(talent.createdAt).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
        : 'Date inconnue';

    const desiredContracts = Array.isArray(talent?.contractType)
        ? talent.contractType.join(', ')
        : talent?.contractType || 'CDI temps plein, CDI temps partiel';
    const availableFrom = talent?.availability || 'Immediatement';
    const genderLabel = GENDER_LABELS[talent?.gender] || 'Genre non renseigné';
    const preferenceTags = Array.isArray(talent?.preferences) ? talent.preferences.slice(0, 3) : [];

    if (formOpen) {
        return <TalentForm
            talent={talent}
            isEdit
            initialFormState="edit"
            onSuccess={() => {
                setFormOpen(false);
            }}
            onFormCancel={() => setFormOpen(false)}
        />
    }

    return (
        <SectionCard
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                padding: '16px',
                flex: 1,
                position: 'relative',
                background: theme.palette.dashboard.main,
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: "space-between" }}>
                        <Typography
                            variant="body2"
                            sx={{
                                background: theme.palette.transparentPurple.main,
                                borderRadius: '16px',
                                padding: '4px 8px',
                                fontWeight: 700,
                                color: theme.palette.neutralWhite.main,
                                fontSize: '13px',
                                lineHeight: '16px',
                            }}>
                            {genderLabel}
                        </Typography>


                        <EditActionButton
                            onClick={() => setFormOpen(true)}
                            sx={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                '&:hover': { backgroundColor: 'transparent' },
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 500,
                            fontSize: '25px',
                            lineHeight: '40px',
                            fontFamily: theme.typography.fontFamilyBrand,
                        }}>
                        {`${talent.firstName || ''} ${talent.lastName || ''}`.trim() || 'Nom non renseigné'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            fontWeight={700}
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                '& i': { fontSize: 14, display: 'flex' },
                            }}>
                            <i className="fi fi-br-marker" />
                            {talent.city || 'Ville non renseignee'}
                        </Typography>
                        <Divider />
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            fontWeight={700}
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                alignItems: 'center',
                                '& i': { fontSize: 14, display: 'flex' },
                            }}>
                            <i className="fi fi-br-clock" />
                            Rejoint en {joinedLabel}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <Button size="small" variant="outlined" color="secondary" startIcon={<i className="fi fi-br-envelope" />} sx={{ flex: 1 }}>
                            Message
                        </Button>
                        <DownloadTalentCvButton
                            professionList={{ professions: [] }}
                            userData={talent}
                            buttonProps={{
                                size: 'small',
                                variant: 'outlined',
                                color: 'secondary',
                                startIcon: <i className="fi fi-br-document" />,
                                sx: { flex: 1 },
                            }}
                        />
                        <IconButton variant="outlined" size="small" color="secondary" sx={{ width: '42px', height: '42px' }}>
                            <i className="fi fi-br-share" />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {preferenceTags.length > 0 ? (
                            preferenceTags.map((tag: string, index: number) => (
                                <Box
                                    key={index}
                                    sx={{
                                        background: theme.palette.textInvert.main,
                                        padding: '8px 12px',
                                        borderRadius: '16px',
                                    }}>
                                    <Typography variant="body2" fontSize="13px" lineHeight="16px" fontWeight={500} color={theme.palette.neutralWhite.main}>
                                        {tag}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: '16px', color: theme.palette.textSecondary.main }}>
                                Aucune preference
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            background: theme.palette.disableFormBlack.main,
                            padding: '8px 12px',
                            borderRadius: '8px',
                            width: '100%',
                        }}>
                        <Typography sx={{ fontSize: '13px', lineHeight: '16px', fontWeight: 500, color: theme.palette.neutralWhite.main }}>
                            Contrats souhaites : {desiredContracts}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', lineHeight: '16px', fontWeight: 500, color: theme.palette.neutralWhite.main }}>
                            Disponible sous : {availableFrom}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </SectionCard>
    );
};

export default TalentProfileCard;
